// lib/safe-date.ts
//
// Canonical date utilities. All user-facing date formatting should go through
// these helpers so timestamps from the database (UTC ISO strings) are always
// displayed in the viewer's local timezone.

/**
 * Parse a date string safely, returning null for invalid values.
 * Handles full ISO strings, date-only strings ("2026-04-14"), and Date objects.
 *
 * Date-only strings (no "T") are treated as LOCAL dates (append T00:00:00)
 * so calendar-date fields like startDate/endDate don't shift days.
 * Full ISO strings (with "T" or "Z") are parsed as-is so the browser
 * correctly converts UTC → local.
 */
export function safeDate(dateString: string | Date | null | undefined): Date | null {
  if (!dateString) return null;
  if (dateString instanceof Date) return isNaN(dateString.getTime()) ? null : dateString;
  // Date-only string (e.g. "2026-04-14") — treat as local midnight
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    const d = new Date(dateString + "T00:00:00");
    return isNaN(d.getTime()) ? null : d;
  }
  // Full ISO / datetime string — parse as-is (UTC when trailing Z)
  const date = new Date(dateString);
  return isNaN(date.getTime()) ? null : date;
}

/**
 * Format a date string for display in the user's local timezone.
 * Returns "N/A" for null/invalid inputs.
 */
export function safeFormatDate(
  dateString: string | Date | null | undefined,
  options?: Intl.DateTimeFormatOptions
): string {
  const date = safeDate(dateString);
  if (!date) return "N/A";
  return date.toLocaleDateString("en-US", options);
}

/**
 * Format a date+time string for display in the user's local timezone.
 * Default output: "Apr 14, 2026, 8:40 PM"
 */
export function safeFormatDateTime(
  dateString: string | Date | null | undefined,
  options?: Intl.DateTimeFormatOptions
): string {
  const date = safeDate(dateString);
  if (!date) return "N/A";
  return date.toLocaleString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    ...options,
  });
}

/**
 * Format time-only for display in the user's local timezone.
 * Default output: "8:40 PM"
 */
export function safeFormatTime(
  dateString: string | Date | null | undefined,
  options?: Intl.DateTimeFormatOptions
): string {
  const date = safeDate(dateString);
  if (!date) return "N/A";
  return date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    ...options,
  });
}